import { Media, Maybe } from 'graphql.generated';
import {
    GalleryImage,
    ResponsiveImgList,
    Format,
    Image
} from './Gallery.types';

export const LISTING_IMAGE_URL_PREFIX = 'https://lid.zoocdn.com/u/';

const dimensionsAvailableFromImageResizer = [
    [480, 360],
    [768, 576],
    [1024, 768],
    [1080, 810],
    [1200, 900],
    [1600, 1200],
    [2400, 1800]
];

type Filename = Maybe<string> | undefined;

type Formatter = (filename: Filename) => ([width, height]: number[]) => {
    width: number;
    src: string;
    type?: string;
};

const originalFormat =
    (filename: Filename) =>
    ([width, height]: number[]) => ({
        width: width,
        src: `${LISTING_IMAGE_URL_PREFIX}${width}/${height}/${filename}`
    });

const webpFormat =
    (filename: Filename) =>
    ([width, height]: number[]) => ({
        width: width,
        src: `${LISTING_IMAGE_URL_PREFIX}${width}/${height}/${filename}:p`,
        type: 'image/webp'
    });

const createImgList = (
    filename: Filename,
    dimensions: number[][],
    formatter: Formatter
): Image[] => dimensions.map(formatter(filename));

const formatters = {
    original: originalFormat,
    webp: webpFormat
};

const mapPropertyImageResponseToGalleryImages = (
    imageSizes: string,
    listingImagesResponse: Media[],
    formats: Format[] = []
) => {
    formats = [...formats, 'original'];

    return listingImagesResponse.reduce((acc: GalleryImage[], image) => {
        const responsiveImgList: ResponsiveImgList = formats.reduce(
            (acc, format: Format) => ({
                ...acc,
                [format]: createImgList(
                    image.filename,
                    dimensionsAvailableFromImageResizer,
                    formatters[format]
                )
            }),
            Object.create(null)
        );

        // Default to high-res image for older browsers not supporting srcset
        const src =
            responsiveImgList.original[responsiveImgList.original.length - 1]
                .src;

        return [
            ...acc,
            {
                src,
                responsiveImgList,
                alt: image.caption || '',
                sizes: imageSizes
            }
        ];
    }, []);
};

export default mapPropertyImageResponseToGalleryImages;
